import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Alert } from "@/components/ui/Alert";
import { Dialog } from "@/components/ui/Dialog";
import { Spinner } from "@/components/ui/Spinner";
import {
  Checkbox,
  GermanDecimalInput,
  SelectInput,
  TextInput,
  TextareaInput,
} from "@/components/ui/Field";
import { ApiError } from "@/lib/apiClient";
import {
  createMahnstufe,
  deleteBasiszinssatz,
  deleteMahnstufe,
  formatMahnungDate,
  getMahnstufeStandardtexte,
  getMahnwesenBerechtigungen,
  getMahnwesenEinstellungen,
  listBasiszinssaetze,
  listMahnstufen,
  saveBasiszinssatz,
  saveMahnwesenBerechtigungen,
  saveMahnwesenEinstellungen,
  sortiereMahnstufen,
  updateMahnstufe,
  VERSANDART_OPTIONS,
  type BasiszinsSatz,
  type BasiszinsStand,
  type Mahnstufe,
  type MahnwesenEinstellungen,
} from "@/lib/mahnwesen";
import { toast } from "@/lib/toast";

const BERECHTIGUNG_LABELS: Record<string, string> = {
  ansehen: "Mahnwesen ansehen",
  erstellen: "Mahnentwürfe erstellen",
  bearbeiten: "Entwürfe bearbeiten",
  finalisieren: "Mahnungen finalisieren",
  versenden: "Mahnungen versenden",
  stornieren: "Mahnungen stornieren",
  zins_ueberschreiben: "Zinssätze überschreiben",
  verzugsbeginn_ueberschreiben: "Verzugsbeginn ändern",
  kosten_aendern: "Mahnkosten ändern",
  sperren_verwalten: "Mahnsperren / Pausen verwalten",
  vereinbarungen_verwalten: "Zahlungsvereinbarungen verwalten",
  einstellungen: "Einstellungen ändern",
};

const LEERE_STUFE: Partial<Mahnstufe> = {
  code: "",
  bezeichnung: "",
  sortierung: 0,
  aktiv: true,
  tage_nach_faelligkeit: 3,
  tage_nach_vorheriger: 7,
  zahlungsfrist_tage: 7,
  betreff: "",
  dokument_titel: "",
  anrede: "Sehr geehrte Damen und Herren,",
  einleitung: "",
  haupttext: "",
  zahlungsaufforderung: "",
  schlusstext: "Mit freundlichen Grüßen",
  hinweistext: "",
  fusstext: "",
  verzugszinsen_aktiv: false,
  mahnkosten_aktiv: false,
  mahnkosten_betrag: 0,
  verzugspauschale_aktiv: false,
  standard_versandart: "email",
  email_betreff: "",
  email_text: "",
  zahlungsfrist_box_anzeigen: false,
};

export function MahnwesenEinstellungenTab() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [einstellungen, setEinstellungen] = useState<MahnwesenEinstellungen | null>(null);
  const [stufen, setStufen] = useState<Mahnstufe[]>([]);
  const [basiszins, setBasiszins] = useState<BasiszinsSatz[]>([]);
  const [basiszinsStand, setBasiszinsStand] = useState<BasiszinsStand | null>(null);
  const [berechtigungen, setBerechtigungen] = useState<Record<string, string[]>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [savingPerms, setSavingPerms] = useState(false);
  const [stufeDialog, setStufeDialog] = useState<{ stufe: Partial<Mahnstufe>; neu: boolean } | null>(null);
  const [busyStufeId, setBusyStufeId] = useState<number | null>(null);
  const [neuGueltigAb, setNeuGueltigAb] = useState("");
  const [neuZinssatz, setNeuZinssatz] = useState(0);
  const [basiszinsBusy, setBasiszinsBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [e, s, bz, perms] = await Promise.all([
        getMahnwesenEinstellungen(),
        listMahnstufen(),
        listBasiszinssaetze(),
        getMahnwesenBerechtigungen().catch(() => ({}) as Record<string, string[]>),
      ]);
      setEinstellungen(e);
      setStufen(s);
      setBasiszins(bz.items);
      setBasiszinsStand(bz.stand);
      setBerechtigungen(perms);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Mahnwesen-Einstellungen konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Deep-Link aus dem Mahnungs-Editor: ?stufe=<id> öffnet direkt den Stufen-Dialog.
  const stufeParam = searchParams.get("stufe");
  useEffect(() => {
    if (!stufeParam || loading) return;
    const stufe = stufen.find((s) => String(s.id) === stufeParam);
    if (stufe) setStufeDialog({ stufe, neu: false });
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        params.delete("stufe");
        return params;
      },
      { replace: true },
    );
  }, [stufeParam, loading, stufen, setSearchParams]);

  async function handleSaveEinstellungen() {
    if (!einstellungen) return;
    setSavingSettings(true);
    try {
      setEinstellungen(await saveMahnwesenEinstellungen(einstellungen));
      toast.success("Mahnwesen-Einstellungen gespeichert.");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Speichern fehlgeschlagen.");
    } finally {
      setSavingSettings(false);
    }
  }

  async function handleSavePerms() {
    setSavingPerms(true);
    try {
      setBerechtigungen(await saveMahnwesenBerechtigungen(berechtigungen));
      toast.success("Berechtigungen gespeichert.");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Speichern fehlgeschlagen.");
    } finally {
      setSavingPerms(false);
    }
  }

  async function handleStufeSave(stufe: Partial<Mahnstufe>, neu: boolean) {
    try {
      if (neu) {
        await createMahnstufe(stufe);
        toast.success("Mahnstufe angelegt.");
      } else if (stufe.id) {
        await updateMahnstufe(stufe.id, stufe);
        toast.success("Mahnstufe gespeichert.");
      }
      setStufeDialog(null);
      setStufen(await listMahnstufen());
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Speichern fehlgeschlagen.");
    }
  }

  async function handleStufeDelete(stufe: Mahnstufe) {
    if (!window.confirm(`Mahnstufe „${stufe.bezeichnung}“ löschen bzw. deaktivieren?`)) return;
    setBusyStufeId(stufe.id);
    try {
      const result = await deleteMahnstufe(stufe.id);
      toast.success(result.deaktiviert ? "Stufe wurde deaktiviert (bereits in Verwendung)." : "Stufe gelöscht.");
      setStufen(await listMahnstufen());
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Löschen fehlgeschlagen.");
    } finally {
      setBusyStufeId(null);
    }
  }

  async function handleMove(index: number, richtung: -1 | 1) {
    const ziel = index + richtung;
    if (ziel < 0 || ziel >= stufen.length) return;
    const ids = stufen.map((s) => s.id);
    const a = ids[index];
    const b = ids[ziel];
    if (a == null || b == null) return;
    ids[index] = b;
    ids[ziel] = a;
    try {
      setStufen(await sortiereMahnstufen(ids));
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Sortierung fehlgeschlagen.");
    }
  }

  async function handleBasiszinsAdd() {
    if (!neuGueltigAb) {
      toast.error("Bitte „Gültig ab“ angeben (01.01. oder 01.07.).");
      return;
    }
    setBasiszinsBusy(true);
    try {
      await saveBasiszinssatz({
        gueltig_ab: neuGueltigAb,
        zinssatz_prozent: neuZinssatz,
        quelle: "Deutsche Bundesbank",
      });
      const bz = await listBasiszinssaetze();
      setBasiszins(bz.items);
      setBasiszinsStand(bz.stand);
      setNeuGueltigAb("");
      setNeuZinssatz(0);
      toast.success("Basiszinssatz gespeichert.");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Speichern fehlgeschlagen.");
    } finally {
      setBasiszinsBusy(false);
    }
  }

  async function handleBasiszinsDelete(satz: BasiszinsSatz) {
    if (!window.confirm(`Basiszinssatz ab ${formatMahnungDate(satz.gueltig_ab)} löschen?`)) return;
    try {
      await deleteBasiszinssatz(satz.id);
      const bz = await listBasiszinssaetze();
      setBasiszins(bz.items);
      setBasiszinsStand(bz.stand);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Löschen fehlgeschlagen.");
    }
  }

  if (loading) {
    return (
      <Card title="Mahnwesen">
        <div className="flex items-center gap-2 text-sm text-label-secondary">
          <Spinner /> Laden …
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-5">
      {error ? <Alert variant="error">{error}</Alert> : null}

      {/* Allgemeine Einstellungen */}
      {einstellungen ? (
        <Card
          title="Mahnwesen – Grundeinstellungen"
          description="Globale Standards. Mahnstufen, Texte und Gebühren werden je Stufe konfiguriert; im Mahnentwurf ist alles individuell übersteuerbar."
          actions={
            <Button type="button" onClick={() => void handleSaveEinstellungen()} disabled={savingSettings}>
              Speichern
            </Button>
          }
        >
          <div className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <Checkbox
                label="Mahnwesen aktiv"
                checked={einstellungen.aktiv}
                onChange={(e) => setEinstellungen({ ...einstellungen, aktiv: e.target.checked })}
              />
              <Checkbox
                label="Gemahnte Rechnungen standardmäßig an Mahn-E-Mail anhängen"
                checked={einstellungen.rechnung_pdf_anhaengen}
                onChange={(e) =>
                  setEinstellungen({ ...einstellungen, rechnung_pdf_anhaengen: e.target.checked })
                }
              />
            </div>

            <fieldset className="space-y-3">
              <legend className="text-sm font-medium text-label">Verzugszinsen (§ 288 BGB)</legend>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="rounded-lg border border-separator/70 p-3">
                  <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-label-secondary">
                    Verbraucher (B2C)
                  </div>
                  <Checkbox
                    label="Verzugszinsen berechnen"
                    checked={einstellungen.verzugszinsen.b2c.verwenden}
                    onChange={(e) =>
                      setEinstellungen({
                        ...einstellungen,
                        verzugszinsen: {
                          ...einstellungen.verzugszinsen,
                          b2c: { ...einstellungen.verzugszinsen.b2c, verwenden: e.target.checked },
                        },
                      })
                    }
                  />
                  <GermanDecimalInput
                    label="Aufschlag auf Basiszins (%-Punkte)"
                    value={einstellungen.verzugszinsen.b2c.aufschlag}
                    onValueChange={(v) =>
                      setEinstellungen({
                        ...einstellungen,
                        verzugszinsen: {
                          ...einstellungen.verzugszinsen,
                          b2c: { ...einstellungen.verzugszinsen.b2c, aufschlag: v },
                        },
                      })
                    }
                    hint="Gesetzlich: 5 Prozentpunkte"
                    className="mt-2"
                  />
                </div>
                <div className="rounded-lg border border-separator/70 p-3">
                  <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-label-secondary">
                    Unternehmen (B2B)
                  </div>
                  <Checkbox
                    label="Verzugszinsen berechnen"
                    checked={einstellungen.verzugszinsen.b2b.verwenden}
                    onChange={(e) =>
                      setEinstellungen({
                        ...einstellungen,
                        verzugszinsen: {
                          ...einstellungen.verzugszinsen,
                          b2b: { ...einstellungen.verzugszinsen.b2b, verwenden: e.target.checked },
                        },
                      })
                    }
                  />
                  <GermanDecimalInput
                    label="Aufschlag auf Basiszins (%-Punkte)"
                    value={einstellungen.verzugszinsen.b2b.aufschlag}
                    onValueChange={(v) =>
                      setEinstellungen({
                        ...einstellungen,
                        verzugszinsen: {
                          ...einstellungen.verzugszinsen,
                          b2b: { ...einstellungen.verzugszinsen.b2b, aufschlag: v },
                        },
                      })
                    }
                    hint="Gesetzlich: 9 Prozentpunkte"
                    className="mt-2"
                  />
                  <Checkbox
                    label="40-€-Verzugspauschale anbieten (§ 288 Abs. 5 BGB)"
                    checked={einstellungen.verzugszinsen.b2b.verzugspauschale}
                    onChange={(e) =>
                      setEinstellungen({
                        ...einstellungen,
                        verzugszinsen: {
                          ...einstellungen.verzugszinsen,
                          b2b: {
                            ...einstellungen.verzugszinsen.b2b,
                            verzugspauschale: e.target.checked,
                          },
                        },
                      })
                    }
                    className="mt-2"
                  />
                </div>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <GermanDecimalInput
                  label="Verzugspauschale (EUR)"
                  value={einstellungen.verzugspauschale_betrag}
                  onValueChange={(v) =>
                    setEinstellungen({ ...einstellungen, verzugspauschale_betrag: v })
                  }
                />
                <Checkbox
                  label="Rechnungen enthalten den Verbraucherhinweis nach § 286 Abs. 3 BGB (30-Tage-Regel auch bei B2C anwenden)"
                  checked={einstellungen.verbraucherhinweis_in_rechnung}
                  onChange={(e) =>
                    setEinstellungen({
                      ...einstellungen,
                      verbraucherhinweis_in_rechnung: e.target.checked,
                    })
                  }
                  className="pt-6"
                />
              </div>
            </fieldset>
          </div>
        </Card>
      ) : null}

      {/* Briefpapier & Layout */}
      <Card
        title="Briefpapier & Dokumentlayout"
        description="Briefpapier, Seitenränder und Schriften für Zahlungserinnerungen und Mahnungen werden im Layout-Konfigurator gepflegt."
      >
        <p className="text-sm text-label-secondary">
          Im Konfigurator den Dokumenttyp <strong className="text-label">„Mahnung / Zahlungserinnerung“</strong> wählen.
          Ist dort kein eigenes Layout gespeichert, wird automatisch das Rechnungs-Layout verwendet.
        </p>
        <p className="mt-2 text-sm text-label-secondary">
          Dort wird auch der Infoblock „Ansprechpartner/in“ konfiguriert – also ob Telefon, Mobil und E-Mail der
          erstellenden Person auf der Mahnung erscheinen.
        </p>
        <Link
          to="/dokumente/layout?typ=mahnung"
          className="mt-3 inline-flex items-center rounded-lg border border-separator-opaque px-3 py-2 text-sm font-medium text-label hover:bg-fill-tertiary"
        >
          Layout für Mahnungen öffnen
        </Link>
      </Card>

      {/* Mahnstufen */}
      <Card
        title="Mahnstufen"
        description="Reihenfolge, Fristen, Texte und Gebühren je Stufe. Platzhalter wie {{Mahnung.gesamtforderung}} oder {{Kunde.name}} werden in Texten ersetzt."
        actions={
          <Button
            type="button"
            variant="secondary"
            onClick={() => setStufeDialog({ stufe: { ...LEERE_STUFE, sortierung: stufen.length + 1 }, neu: true })}
          >
            Neue Stufe
          </Button>
        }
      >
        {stufen.length === 0 ? (
          <p className="text-sm text-label-secondary">Keine Mahnstufen konfiguriert.</p>
        ) : (
          <ul className="divide-y divide-separator/60">
            {stufen.map((stufe, idx) => (
              <li key={stufe.id} className="flex flex-wrap items-center gap-3 py-2.5">
                <div className="flex items-center gap-1">
                  <Button type="button" size="sm" variant="ghost" disabled={idx === 0} onClick={() => void handleMove(idx, -1)}>
                    ↑
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    disabled={idx === stufen.length - 1}
                    onClick={() => void handleMove(idx, 1)}
                  >
                    ↓
                  </Button>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium text-label">{stufe.bezeichnung}</span>
                    <span className="rounded-full bg-fill-tertiary px-2 py-0.5 text-[11px] text-label-secondary">
                      {stufe.code}
                    </span>
                    {!stufe.aktiv ? (
                      <span className="rounded-full bg-status-red/15 px-2 py-0.5 text-[11px] font-medium text-status-red">
                        Inaktiv
                      </span>
                    ) : null}
                  </div>
                  <div className="text-xs text-label-secondary">
                    {idx === 0
                      ? `${stufe.tage_nach_faelligkeit} Tage nach Fälligkeit`
                      : `${stufe.tage_nach_vorheriger} Tage nach vorheriger Stufe`}
                    {" · "}Zahlungsfrist {stufe.zahlungsfrist_tage} Tage
                    {stufe.mahnkosten_aktiv ? ` · Mahnkosten ${stufe.mahnkosten_betrag.toFixed(2).replace(".", ",")} €` : ""}
                    {stufe.verzugszinsen_aktiv ? " · Verzugszinsen" : ""}
                    {stufe.verzugspauschale_aktiv ? " · Verzugspauschale" : ""}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button type="button" size="sm" variant="secondary" onClick={() => setStufeDialog({ stufe, neu: false })}>
                    Bearbeiten
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    loading={busyStufeId === stufe.id}
                    onClick={() => void handleStufeDelete(stufe)}
                  >
                    Löschen
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {/* Basiszinssätze */}
      <Card
        title="Basiszinssätze (§ 247 BGB)"
        description="Halbjährliche Sätze der Deutschen Bundesbank (Stand 01.01. und 01.07.). Grundlage für gesetzliche Verzugszinsen – auch rückwirkend für Zinsläufe über mehrere Perioden."
      >
        {basiszinsStand && !basiszinsStand.aktuell_gueltig ? (
          <Alert variant="warning" className="mb-3">
            Für den aktuellen Zeitraum ist kein Satz gepflegt
            {basiszinsStand.gepflegt_bis
              ? ` (gepflegt bis ${formatMahnungDate(basiszinsStand.gepflegt_bis)})`
              : ""}
            . Bitte den aktuellen Basiszinssatz eintragen.
          </Alert>
        ) : null}
        <div className="mb-4 flex flex-wrap items-end gap-3">
          <TextInput
            label="Gültig ab"
            type="date"
            value={neuGueltigAb}
            onChange={(e) => setNeuGueltigAb(e.target.value)}
            className="w-44"
          />
          <GermanDecimalInput
            label="Basiszinssatz (%)"
            value={neuZinssatz}
            onValueChange={setNeuZinssatz}
            className="w-40"
          />
          <Button type="button" loading={basiszinsBusy} onClick={() => void handleBasiszinsAdd()}>
            Hinzufügen
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead className="text-xs uppercase tracking-wide text-label-secondary">
              <tr>
                <th className="py-1.5 pr-4 font-semibold">Gültig ab</th>
                <th className="py-1.5 pr-4 text-right font-semibold">Basiszins</th>
                <th className="py-1.5 pr-4 text-right font-semibold">B2C (+5)</th>
                <th className="py-1.5 pr-4 text-right font-semibold">B2B (+9)</th>
                <th className="py-1.5 pr-4 font-semibold">Quelle</th>
                <th className="py-1.5 font-semibold" />
              </tr>
            </thead>
            <tbody>
              {basiszins.map((satz) => (
                <tr key={satz.id} className="border-t border-separator/60">
                  <td className="py-1.5 pr-4 tabular-nums">{formatMahnungDate(satz.gueltig_ab)}</td>
                  <td className="py-1.5 pr-4 text-right tabular-nums">
                    {satz.zinssatz_prozent.toFixed(2).replace(".", ",")} %
                  </td>
                  <td className="py-1.5 pr-4 text-right tabular-nums text-label-secondary">
                    {(satz.zinssatz_prozent + 5).toFixed(2).replace(".", ",")} %
                  </td>
                  <td className="py-1.5 pr-4 text-right tabular-nums text-label-secondary">
                    {(satz.zinssatz_prozent + 9).toFixed(2).replace(".", ",")} %
                  </td>
                  <td className="py-1.5 pr-4 text-xs text-label-secondary">{satz.quelle || "—"}</td>
                  <td className="py-1.5 text-right">
                    <Button type="button" size="sm" variant="ghost" onClick={() => void handleBasiszinsDelete(satz)}>
                      Löschen
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Berechtigungen */}
      <Card
        title="Berechtigungen"
        description="Welche LDAP-Gruppen dürfen welche Mahnwesen-Aktion ausführen (mehrere Gruppen mit Komma trennen). App-Admins dürfen immer alles."
        actions={
          <Button type="button" onClick={() => void handleSavePerms()} disabled={savingPerms}>
            Speichern
          </Button>
        }
      >
        <div className="grid gap-3 sm:grid-cols-2">
          {Object.entries(BERECHTIGUNG_LABELS).map(([key, label]) => (
            <TextInput
              key={key}
              label={label}
              value={(berechtigungen[key] || []).join(", ")}
              onChange={(e) =>
                setBerechtigungen({
                  ...berechtigungen,
                  [key]: e.target.value
                    .split(",")
                    .map((g) => g.trim())
                    .filter(Boolean),
                })
              }
              placeholder="Büro, Abrechnung …"
            />
          ))}
        </div>
      </Card>

      {stufeDialog ? (
        <StufeDialog
          stufe={stufeDialog.stufe}
          neu={stufeDialog.neu}
          onClose={() => setStufeDialog(null)}
          onSave={(s) => void handleStufeSave(s, stufeDialog.neu)}
        />
      ) : null}
    </div>
  );
}

function StufeDialog({
  stufe,
  neu,
  onClose,
  onSave,
}: {
  stufe: Partial<Mahnstufe>;
  neu: boolean;
  onClose: () => void;
  onSave: (stufe: Partial<Mahnstufe>) => void;
}) {
  const [form, setForm] = useState<Partial<Mahnstufe>>({ ...stufe });
  const [ladeStandard, setLadeStandard] = useState(false);

  function set<K extends keyof Mahnstufe>(key: K, value: Mahnstufe[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function handleStandardtexte() {
    const code = String(form.code || "").trim();
    if (!code) {
      toast.error("Bitte zuerst den Code der Stufe eintragen.");
      return;
    }
    if (
      !window.confirm(
        "Dokument- und E-Mail-Texte dieser Stufe durch die mitgelieferten Standardtexte ersetzen? Eigene Formulierungen gehen verloren (erst mit „Speichern“ wirksam).",
      )
    )
      return;
    setLadeStandard(true);
    try {
      const texte = await getMahnstufeStandardtexte(code, form.sortierung);
      setForm((prev) => ({ ...prev, ...texte }));
      toast.success("Standardtexte übernommen – bitte prüfen und speichern.");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Standardtexte konnten nicht geladen werden.");
    } finally {
      setLadeStandard(false);
    }
  }

  return (
    <Dialog
      open
      onClose={onClose}
      title={neu ? "Neue Mahnstufe" : `Mahnstufe „${stufe.bezeichnung}“ bearbeiten`}
      size="2xl"
      footer={
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Abbrechen
          </Button>
          <Button
            type="button"
            onClick={() => {
              if (!String(form.code || "").trim() || !String(form.bezeichnung || "").trim()) {
                toast.error("Code und Bezeichnung sind Pflichtfelder.");
                return;
              }
              onSave(form);
            }}
          >
            Speichern
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-3">
          <TextInput
            label="Code (technisch)"
            value={form.code || ""}
            onChange={(e) => set("code", e.target.value)}
            disabled={!neu}
            hint={neu ? "z. B. erinnerung, mahnung_1" : "Nicht änderbar"}
          />
          <TextInput
            label="Bezeichnung (auf Dokument)"
            value={form.bezeichnung || ""}
            onChange={(e) => set("bezeichnung", e.target.value)}
          />
          <TextInput
            label="Interne Bezeichnung"
            value={form.interne_bezeichnung || ""}
            onChange={(e) => set("interne_bezeichnung", e.target.value)}
          />
        </div>
        <div className="grid gap-3 sm:grid-cols-4">
          <TextInput
            label="Tage nach Fälligkeit"
            type="number"
            min={0}
            value={String(form.tage_nach_faelligkeit ?? 3)}
            onChange={(e) => set("tage_nach_faelligkeit", Math.max(0, Math.round(Number(e.target.value) || 0)))}
            hint="Für die erste Stufe"
          />
          <TextInput
            label="Tage nach vorheriger Stufe"
            type="number"
            min={0}
            value={String(form.tage_nach_vorheriger ?? 7)}
            onChange={(e) => set("tage_nach_vorheriger", Math.max(0, Math.round(Number(e.target.value) || 0)))}
          />
          <TextInput
            label="Zahlungsfrist (Tage)"
            type="number"
            min={1}
            value={String(form.zahlungsfrist_tage ?? 7)}
            onChange={(e) => set("zahlungsfrist_tage", Math.max(1, Math.round(Number(e.target.value) || 1)))}
          />
          <SelectInput
            label="Standard-Versand"
            value={form.standard_versandart || "email"}
            onChange={(e) => set("standard_versandart", e.target.value)}
          >
            {VERSANDART_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </SelectInput>
        </div>

        <div className="grid gap-3 sm:grid-cols-3">
          <Checkbox
            label="Stufe aktiv"
            checked={Boolean(form.aktiv)}
            onChange={(e) => set("aktiv", e.target.checked)}
          />
          <Checkbox
            label="Verzugszinsen ausweisen"
            checked={Boolean(form.verzugszinsen_aktiv)}
            onChange={(e) => set("verzugszinsen_aktiv", e.target.checked)}
          />
          <Checkbox
            label="Verzugspauschale (B2B)"
            checked={Boolean(form.verzugspauschale_aktiv)}
            onChange={(e) => set("verzugspauschale_aktiv", e.target.checked)}
          />
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <Checkbox
            label="Mahnkosten berechnen"
            checked={Boolean(form.mahnkosten_aktiv)}
            onChange={(e) => set("mahnkosten_aktiv", e.target.checked)}
            className="pt-6"
          />
          <GermanDecimalInput
            label="Mahnkosten (EUR)"
            value={form.mahnkosten_betrag ?? 0}
            onValueChange={(v) => set("mahnkosten_betrag", v)}
            hint="Nur tatsächlich entstandene Kosten (Porto, Material) sind sicher durchsetzbar."
          />
        </div>

        <fieldset className="space-y-3">
          <legend className="sr-only">Dokumenttexte</legend>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="text-sm font-medium text-label">Dokumenttexte</span>
            <Button type="button" size="sm" variant="ghost" loading={ladeStandard} onClick={() => void handleStandardtexte()}>
              Standardtexte übernehmen
            </Button>
          </div>
          <p className="text-xs text-label-tertiary">
            Reihenfolge im Brief: Titel → Betreff → Anrede → Einleitung → Forderungstabelle → Haupttext →
            Zahlungsaufforderung → Schlusstext → Hinweis → Fußtext. Platzhalter: {"{{Mahnung.stufe}}"},{" "}
            {"{{Mahnung.gesamtforderung}}"}, {"{{Mahnung.zahlungsfrist}}"}, {"{{Mahnung.rechnungsnummern}}"},{" "}
            {"{{Mahnung.vorherige}}"} (Vorgängerschreiben),{" "}
            {"{{Ansprechpartner.briefanrede}}"}, {"{{Kunde.name}}"}, {"{{Firma.name}}"} …
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            <TextInput
              label="Dokumenttitel"
              value={form.dokument_titel || ""}
              onChange={(e) => set("dokument_titel", e.target.value)}
              placeholder="z. B. {{Mahnung.stufe}}"
              hint="Leer = Bezeichnung der Stufe."
            />
            <TextInput
              label="Betreffzeile (optional)"
              value={form.betreff || ""}
              onChange={(e) => set("betreff", e.target.value)}
              hint="Leer lassen – die Rechnungsnummern stehen bereits in der Tabelle."
            />
          </div>
          <TextInput
            label="Anrede"
            value={form.anrede || ""}
            onChange={(e) => set("anrede", e.target.value)}
            hint="Entfällt automatisch, wenn die Einleitung selbst mit einer Anrede beginnt (z. B. {{Ansprechpartner.briefanrede}})."
          />
          <TextareaInput
            label="Einleitung"
            rows={3}
            value={form.einleitung || ""}
            onChange={(e) => set("einleitung", e.target.value)}
            hint="Vor der Forderungstabelle."
          />
          <TextareaInput
            label="Haupttext"
            rows={4}
            value={form.haupttext || ""}
            onChange={(e) => set("haupttext", e.target.value)}
            hint="Nach der Tabelle – Verzugshinweis, angekündigte Schritte."
          />
          <TextareaInput
            label="Zahlungsaufforderung"
            rows={2}
            value={form.zahlungsaufforderung || ""}
            onChange={(e) => set("zahlungsaufforderung", e.target.value)}
            hint="Ein Satz mit Frist und Konto – Frist und „überweisen“ nicht zusätzlich im Haupttext wiederholen."
          />
          <Checkbox
            label="Zahlungsfrist zusätzlich als hervorgehobenen Kasten drucken"
            description="Standard: aus. Nur einschalten, wenn die Zahlungsaufforderung den Termin nicht selbst nennt – sonst steht das Datum doppelt."
            checked={Boolean(form.zahlungsfrist_box_anzeigen)}
            onChange={(e) => set("zahlungsfrist_box_anzeigen", e.target.checked)}
          />
          <div className="grid gap-3 sm:grid-cols-2">
            <TextareaInput
              label="Schlusstext"
              rows={2}
              value={form.schlusstext || ""}
              onChange={(e) => set("schlusstext", e.target.value)}
              hint="Grußformel."
            />
            <TextareaInput
              label="Hinweistext (klein)"
              rows={2}
              value={form.hinweistext || ""}
              onChange={(e) => set("hinweistext", e.target.value)}
              hint="z. B. der „gegenstandslos“-Satz – nur hier, nicht zusätzlich im Schlusstext."
            />
          </div>
          <TextareaInput
            label="Fußtext"
            rows={2}
            value={form.fusstext || ""}
            onChange={(e) => set("fusstext", e.target.value)}
          />
        </fieldset>

        <fieldset className="space-y-3">
          <legend className="text-sm font-medium text-label">E-Mail-Vorlage</legend>
          <TextInput
            label="E-Mail-Betreff"
            value={form.email_betreff || ""}
            onChange={(e) => set("email_betreff", e.target.value)}
          />
          <TextareaInput
            label="E-Mail-Text"
            rows={5}
            value={form.email_text || ""}
            onChange={(e) => set("email_text", e.target.value)}
          />
        </fieldset>
      </div>
    </Dialog>
  );
}
